/* The student's own plan, and the plans on sale. */

/* ------------------------------------------------------- what I hold */

/*
  The balance leads the page: a student opening this almost always wants
  "how many sessions have I got left", and only sometimes wants to buy.
  Tinted so it reads as a statement about them rather than another card in
  a list of offers.
*/
.mine {
  display: flex;
  flex-direction: column;
  /* Trimmed from --space-4/-5: the card was tall enough to push the plan
     list below the fold on a laptop. A small reduction, not a redesign. */
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}

.mineHead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
}

.mineLabel {
  display: block;
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-subtle);
}

.mineName {
  margin: var(--space-1) 0 0;
  font-size: var(--text-xl);
  font-weight: 700;
  color: var(--text);
}

.mineMessage {
  margin: 0;
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text-muted);
}

.mineExpiry {
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
}

/*
  "No money was taken." Green, because it is the answer to the question the
  student actually has, and a grey aside would read as fine print.
*/
.mineReassure {
  align-self: flex-start;
  margin: 0;
  padding: var(--space-1) var(--space-3);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--success);
  background: var(--success-soft);
  border-radius: var(--radius-full);
}

.mineAction {
  display: flex;
}

/*
  Four numbers in a row. `auto-fit` rather than a fixed four so they stack
  in pairs on a phone instead of shrinking to unreadable columns.
*/
.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(5rem, 1fr));
  gap: var(--space-3);
  padding: var(--space-3) 0;
  border-block: 1px solid var(--border);
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.statValue {
  font-size: var(--text-xl);
  font-weight: 700;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  color: var(--text);
}

.statLabel {
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
}

/* ---------------------------------------------------- plans on sale */

.sectionTitle {
  margin: 0 0 var(--space-4);
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--text);
}

.planGrid {
  display: grid;
  gap: var(--space-4);
  /* Capped, so one plan is a card rather than a banner across a wide
     screen — the same rule as the mentor grid. */
  grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr));
}

.plan {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-5);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  transition:
    border-color var(--transition-fast),
    box-shadow var(--transition-fast);
}

.plan:hover {
  border-color: var(--border-strong);
  box-shadow: var(--shadow-md);
}

.planHead {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
}

.planName {
  margin: 0;
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--text);
}

/*
  `total_amount`, never `price` — see the comment in SubscriptionCard. The
  figure is the commitment, so it carries the weight.
*/
.planPrice {
  font-size: var(--text-xl);
  font-weight: 700;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  color: var(--text);
}

.planDesc {
  margin: 0;
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text-muted);
}

.planFacts {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-4);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.planFacts strong {
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

/* "Why is it more than the plan price" — answered here, not at a counter. */
.breakdown {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: 0;
  padding: var(--space-3) 0 0;
  border-top: 1px solid var(--border);
  list-style: none;
  font-size: var(--text-xs);
  color: var(--text-muted);
}

.breakdown li {
  display: flex;
  justify-content: space-between;
  gap: var(--space-3);
}

/* ------------------------------------------------------- past plans */

/*
  A list, not cards. These are records to scan, and card styling would
  give each the weight of the current plan above them.
*/
.history {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

.historyRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}

.historyMain {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.historyName {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--text);
}

/* The one still running, marked so the dates need not be compared. */
.historyNow {
  padding: 1px var(--space-2);
  font-size: var(--text-xs);
  font-weight: 500;
  color: var(--success);
  background: var(--success-soft);
  border-radius: var(--radius-full);
}

.historyWhen {
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.historyFacts {
  display: flex;
  align-items: baseline;
  gap: var(--space-4);
  font-variant-numeric: tabular-nums;
}

.historyUsed {
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.historyPaid {
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--text);
}

.historyMore {
  margin-top: var(--space-3);
  padding: 0;
  font: inherit;
  font-size: var(--text-sm);
  color: var(--indigo);
  background: none;
  border: 0;
  text-decoration: underline;
  cursor: pointer;
}

/* ------------------------------------------------- the payment wait */

.waitWrap {
  display: grid;
  place-items: center;
  padding: var(--space-6) 0;
}

.waitCard {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-4);
  max-width: 28rem;
  padding: var(--space-6);
  text-align: center;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.waitTitle {
  margin: 0;
  font-size: var(--text-xl);
  font-weight: 700;
  color: var(--text);
}

.waitNote {
  margin: 0;
  font-size: var(--text-base);
  line-height: var(--leading-normal);
  color: var(--text-muted);
}

/* "It is safe to leave" — quieter, because it is reassurance, not news. */
.waitFine {
  margin: 0;
  font-size: var(--text-xs);
  line-height: var(--leading-normal);
  color: var(--text-subtle);
}

.waitActions {
  display: flex;
  gap: var(--space-3);
}

.spinner {
  width: 2rem;
  height: 2rem;
  border: 3px solid var(--border);
  border-top-color: var(--indigo);
  border-radius: var(--radius-full);
  animation: spin 800ms linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/*
  Respected: the heading already says what is happening, so losing the
  animation costs nothing.
*/
@media (prefers-reduced-motion: reduce) {
  .spinner {
    animation: none;
  }

  .plan {
    transition: none;
  }
}

/* ---------------------------------------------------------- shared */

.muted {
  margin: 0;
  color: var(--text-muted);
  font-size: var(--text-sm);
}

.error {
  margin: 0;
  color: var(--danger);
  font-size: var(--text-sm);
}
